import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { ESTADOS_BRASIL } from '../../constants/estados';
import { Icon } from '../../components/participant/Icon';
import { participantColors as c, fadeUpKeyframes } from '../../theme/participantTheme';
import logo from '../../logo.png';

interface RegisterFormData {
  name: string;
  password: string;
  confirmPassword: string;
  cpf: string;
  city: string;
  state: string;
  phone?: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<RegisterFormData>({
    name: '',
    password: '',
    confirmPassword: '',
    cpf: '',
    city: '',
    state: '',
    phone: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Máscaras progressivas — o backend exige o formato exato (CPF
  // 000.000.000-00, telefone (00) 00000-0000), então formatamos enquanto
  // o usuário digita em vez de só validar no fim.
  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length === 0) return '';
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const formatCpf = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'phone' ? formatPhone(value) : name === 'cpf' ? formatCpf(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Senhas não coincidem');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // confirmPassword é só para validação local — o backend nunca deve
      // recebê-lo (e agora rejeita a requisição inteira se receber um campo
      // que o DTO não declara).
      const { confirmPassword, ...registerData } = formData;

      await api.post('/users/register', registerData);
      showSnackbar('Cadastro realizado com sucesso!', 'success');
      navigate('/login');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erro ao realizar cadastro';
      setError(Array.isArray(message) ? message.join(', ') : message);
      showSnackbar(Array.isArray(message) ? message.join(', ') : message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputBoxSx = (hasError: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    border: `2px solid ${hasError ? c.error : c.border}`,
    borderRadius: `${c.radius.input}px`,
    background: c.surface,
    pl: '14px',
  });

  const inputElSx = {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontFamily: c.fontFamily,
    fontSize: 17,
    fontWeight: 600,
    color: c.textBase,
    padding: '15px 14px 15px 8px',
    minWidth: 0,
    appearance: 'none' as const,
  };

  const labelSx = { fontSize: 15.5, fontWeight: 800, color: c.textDarkest };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: c.gradients.pageBg,
        fontFamily: c.fontFamily,
        color: c.textBase,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px 48px 16px',
      }}
    >
      <style>{fadeUpKeyframes}</style>

      <Box
        sx={{
          width: '100%',
          maxWidth: 460,
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          animation: 'participantFadeUp 0.4s ease both',
        }}
      >
        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: '4px' }}>
          <Box component="img" src={logo} alt="ACIAG" sx={{ height: 68, width: 'auto', objectFit: 'contain' }} />
        </Box>

        {/* Card */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            background: c.surface,
            borderRadius: `${c.radius.cardLarge}px`,
            padding: '28px 24px',
            boxShadow: c.shadows.loginCard,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Box component="div" sx={{ fontSize: 27, fontWeight: 800, color: c.textDarkest, lineHeight: 1.15 }}>
              Criar minha conta
            </Box>
            <Box component="div" sx={{ fontSize: 16, color: c.textSecondary, lineHeight: 1.45 }}>
              Registre-se para participar de campanhas e sorteios
            </Box>
          </Box>

          {/* Nome completo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box component="label" sx={labelSx}>
              Nome completo
            </Box>
            <Box sx={inputBoxSx(false)}>
              <Icon name="person" size={22} color={c.brandIcon} />
              <Box
                component="input"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                autoComplete="name"
                placeholder="Seu nome completo"
                disabled={loading}
                sx={inputElSx}
              />
            </Box>
          </Box>

          {/* CPF + Telefone */}
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={labelSx}>
                CPF
              </Box>
              <Box sx={inputBoxSx(false)}>
                <Icon name="badge" size={22} color={c.brandIcon} />
                <Box
                  component="input"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleChange}
                  required
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  disabled={loading}
                  sx={inputElSx}
                />
              </Box>
            </Box>

            <Box sx={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={labelSx}>
                Telefone
              </Box>
              <Box sx={inputBoxSx(false)}>
                <Icon name="call" size={22} color={c.brandIcon} />
                <Box
                  component="input"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  inputMode="tel"
                  disabled={loading}
                  sx={inputElSx}
                />
              </Box>
            </Box>
          </Box>

          {/* Cidade + Estado */}
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={labelSx}>
                Cidade
              </Box>
              <Box sx={inputBoxSx(false)}>
                <Icon name="location_on" size={22} color={c.brandIcon} />
                <Box
                  component="input"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  placeholder="Sua cidade"
                  disabled={loading}
                  sx={inputElSx}
                />
              </Box>
            </Box>

            <Box sx={{ flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box component="label" sx={labelSx}>
                Estado
              </Box>
              <Box sx={inputBoxSx(false)}>
                <Icon name="map" size={22} color={c.brandIcon} />
                <Box
                  component="select"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  sx={{ ...inputElSx, cursor: loading ? 'default' : 'pointer' }}
                >
                  <option value="">Selecione...</option>
                  {ESTADOS_BRASIL.map((estado) => (
                    <option key={estado} value={estado}>
                      {estado}
                    </option>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Senha */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box component="label" sx={labelSx}>
              Senha
            </Box>
            <Box sx={{ ...inputBoxSx(false), padding: '0 6px 0 14px' }}>
              <Icon name="lock" size={22} color={c.brandIcon} />
              <Box
                component="input"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                disabled={loading}
                sx={{ ...inputElSx, padding: '15px 8px' }}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                title="Mostrar/esconder senha"
                sx={{
                  width: 44,
                  height: 44,
                  border: 'none',
                  borderRadius: '10px',
                  background: 'transparent',
                  color: c.brandIcon,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': { background: c.surfaceHover },
                }}
              >
                <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={22} />
              </Box>
            </Box>
            <Box component="div" sx={{ fontSize: 13, color: c.textTertiary, lineHeight: 1.4, mt: '2px' }}>
              Mínimo de 8 caracteres.
            </Box>
          </Box>

          {/* Confirmar senha */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box component="label" sx={labelSx}>
              Confirmar senha
            </Box>
            <Box sx={{ ...inputBoxSx(false), padding: '0 6px 0 14px' }}>
              <Icon name="lock" size={22} color={c.brandIcon} />
              <Box
                component="input"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                disabled={loading}
                sx={{ ...inputElSx, padding: '15px 8px' }}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                title="Mostrar/esconder senha"
                sx={{
                  width: 44,
                  height: 44,
                  border: 'none',
                  borderRadius: '10px',
                  background: 'transparent',
                  color: c.brandIcon,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': { background: c.surfaceHover },
                }}
              >
                <Icon name={showConfirmPassword ? 'visibility_off' : 'visibility'} size={22} />
              </Box>
            </Box>
          </Box>

          {error && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: c.errorBg,
                border: `2px solid ${c.errorBorder}`,
                borderRadius: `${c.radius.input}px`,
                padding: '13px 15px',
              }}
            >
              <Icon name="error" size={22} color={c.error} />
              <Box component="span" sx={{ fontSize: 15, fontWeight: 700, color: c.errorTextDark, lineHeight: 1.4 }}>
                {error}
              </Box>
            </Box>
          )}

          <Box
            component="button"
            type="submit"
            disabled={loading}
            sx={{
              width: '100%',
              minHeight: 62,
              border: 'none',
              borderRadius: `${c.radius.button}px`,
              background: c.gradients.primaryButton,
              color: c.onDarkPrimary,
              fontFamily: c.fontFamily,
              fontSize: 19,
              fontWeight: 800,
              cursor: loading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: c.shadows.primaryButton,
              transition: 'transform 0.15s',
              opacity: loading ? 0.85 : 1,
              mt: '4px',
              '&:hover': loading ? {} : { transform: 'translateY(-2px)' },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: c.onDarkPrimary }} />
            ) : (
              <>
                <Icon name="person_add" size={24} />
                Criar Conta
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box sx={{ flex: 1, height: '1px', background: c.border }} />
            <Box sx={{ fontSize: 14, fontWeight: 700, color: c.textTertiary }}>Já tem uma conta?</Box>
            <Box sx={{ flex: 1, height: '1px', background: c.border }} />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => navigate('/login')}
            disabled={loading}
            sx={{
              width: '100%',
              minHeight: 58,
              border: `2px solid ${c.borderHover}`,
              borderRadius: `${c.radius.button}px`,
              background: c.surface,
              color: c.brandDark,
              fontFamily: c.fontFamily,
              fontSize: 18,
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              '&:hover': { background: c.surfaceHover },
            }}
          >
            <Icon name="login" size={24} />
            Fazer Login
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', fontSize: 13.5, color: c.textTertiary, fontWeight: 600 }}>
          Sistema de Sorteios — Associação Comercial © 2026
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
